import { useCallback, useState } from 'react';
import axios from 'axios';
import api, {
  API_ERRORS,
  getApiErrorName,
  getApiErrorStatus,
} from '../services/api';
import {
  prepareMediaForUpload,
  UnsupportedFileError,
} from '../utils/prepareMediaForUpload';
import { getVideoDuration } from '../utils/mediaConstraints';

export type ModerationStatus =
  | 'UPLOADING'
  | 'APPROVED'
  | 'REJECTED'
  | 'UNSUPPORTED'
  | 'ERROR';

export interface ModerationEntry {
  /** Matches the file id from FileUploadInput */
  fileId: string;
  status: ModerationStatus;
  /** S3 final key returned on approval */
  finalKey?: string;
  /** Public URL of the approved image */
  publicUrl?: string;
  /** Human-readable message / rejection reason */
  message?: string;
  /**
   * Liberado para publicar, mas ainda sem veredito humano: o vídeo, que entra
   * no ar com a tag "Em avaliação", e a foto que o Rekognition reteve, que
   * fica invisível até alguém aprovar. Nos dois casos o post vai ao ar agora.
   */
  requiresReview?: boolean;
}

interface UseImageModerationReturn {
  /** Map of fileId → ModerationEntry */
  entries: Record<string, ModerationEntry>;
  /** Trigger moderation analysis for a file */
  analyzeFile: (fileId: string, file: File) => Promise<void>;
  /** Remove a moderation entry (e.g. when the user deletes a file) */
  removeEntry: (fileId: string) => void;
  /** True when every arquivo processável foi aprovado */
  allApproved: boolean;
  /** True enquanto algum arquivo ainda está subindo */
  hasPending: boolean;
  /** True when at least one file was REJECTED */
  hasRejected: boolean;
  /** True quando algum arquivo tem formato que não sabemos processar */
  hasUnsupported: boolean;
  /** True quando algum anexo publica já, mas aguarda avaliação humana */
  hasMediaAwaitingReview: boolean;
  /** Get the list of approved temp_keys / final_keys for form submission */
  approvedKeys: string[];
}

export function useImageModeration(): UseImageModerationReturn {
  const [entries, setEntries] = useState<Record<string, ModerationEntry>>({});
  const updateEntry = useCallback(
    (fileId: string, patch: Partial<ModerationEntry>) => {
      setEntries(prev => ({
        ...prev,
        [fileId]: { ...prev[fileId], ...patch },
      }));
    },
    [],
  );

  const analyzeFile = useCallback(
    async (fileId: string, file: File) => {
      updateEntry(fileId, { fileId, status: 'UPLOADING' });

      // Normaliza antes de qualquer chamada de rede: vídeo sobe como está,
      // foto vira JPEG/PNG, e formato ilegível para aqui sem gastar uma ida
      // ao backend nem virar "Rejeitado".
      let uploadFile: File;

      try {
        uploadFile = await prepareMediaForUpload(file);
      } catch (err) {
        if (err instanceof UnsupportedFileError) {
          updateEntry(fileId, { status: 'UNSUPPORTED', message: err.message });
          return;
        }
        updateEntry(fileId, {
          status: 'ERROR',
          message: 'Erro ao preparar o arquivo.',
        });
        return;
      }

      try {
        // O backend recusa vídeo longo demais antes de o upload começar.
        const durationSeconds = uploadFile.type.startsWith('video/')
          ? await getVideoDuration(uploadFile)
          : null;

        // ── Phase 1: solicita presigned PUT URL ───────────────────────────
        const { data: presigned } = await api.get('/posts/presigned-url', {
          params: {
            filename: uploadFile.name,
            size_bytes: uploadFile.size,
            ...(durationSeconds !== null && {
              duration_seconds: Math.ceil(durationSeconds),
            }),
          },
        });
        const { temp_key, upload_url, content_type } = presigned;

        // ── Phase 1: PUT direto para o S3 ─────────────────────────────────
        //
        // O Content-Type precisa ser o mesmo com que o backend assinou a URL.
        // Antes daqui o header era removido, e o S3 gravava o default do axios
        // (application/x-www-form-urlencoded) como tipo do objeto: imagem
        // ainda aparecia, porque o <img> fareja os bytes, mas vídeo baixava em
        // vez de tocar e o Slack se recusava a renderizar a foto.
        await axios.put(upload_url, uploadFile, {
          headers: {
            'Content-Type': content_type,
            // Impede que o axios herde um Bearer de defaults globais
            Authorization: undefined as unknown as string,
          },
          // O corpo é um File e sobe como está; sem isto o axios tentaria
          // serializá-lo conforme o Content-Type declarado.
          transformRequest: [(data: unknown) => data],
        });

        // ── Phase 2: pede análise ao Rekognition ──────────────────────────
        const response = await api.post('/posts/analyze', { temp_key });

        const { status: httpStatus } = response;
        const { status, final_key, public_url, requires_human_review } =
          response.data;

        if (httpStatus === 200 && status === 'APPROVED') {
          updateEntry(fileId, {
            status: 'APPROVED',
            finalKey: final_key,
            publicUrl: public_url,
            requiresReview: !!requires_human_review,
            message: requires_human_review
              ? 'Seu post publica na hora; este arquivo passa por avaliação.'
              : undefined,
          });
        }
      } catch (err: any) {
        const httpStatus = getApiErrorStatus(err);
        const serverMessage =
          err?.response?.data?.message || err?.response?.data?.reason;

        const errorName = getApiErrorName(err);

        // 415 e 413 são problema do arquivo — formato ou tamanho —, não do
        // conteúdo. Só o 400 vem da moderação e merece "Rejeitado".
        if (
          httpStatus === 415 ||
          httpStatus === 413 ||
          errorName === API_ERRORS.UNSUPPORTED_MEDIA_TYPE ||
          errorName === API_ERRORS.MEDIA_TOO_LARGE
        ) {
          updateEntry(fileId, {
            status: 'UNSUPPORTED',
            message:
              serverMessage ||
              'Formato não suportado. Envie a foto como JPG ou PNG.',
          });
        } else if (httpStatus === 400) {
          updateEntry(fileId, {
            status: 'REJECTED',
            message: serverMessage || 'Imagem rejeitada pela moderação.',
          });
        } else {
          updateEntry(fileId, {
            status: 'ERROR',
            message: 'Erro ao analisar o arquivo.',
          });
        }
      }
    },
    [updateEntry],
  );

  const removeEntry = useCallback((fileId: string) => {
    setEntries(prev => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
  }, []);

  const entryValues = Object.values(entries);

  // Arquivos com formato ilegível não entram na conta: eles não são enviados,
  // e não devem impedir que as fotos válidas do mesmo post sejam publicadas.
  const processableEntries = entryValues.filter(
    e => e.status !== 'UNSUPPORTED',
  );

  const allApproved =
    processableEntries.length > 0 &&
    processableEntries.every(e => e.status === 'APPROVED');

  const hasPending = entryValues.some(e => e.status === 'UPLOADING');

  const hasRejected = entryValues.some(e => e.status === 'REJECTED');

  const hasUnsupported = entryValues.some(e => e.status === 'UNSUPPORTED');

  const hasMediaAwaitingReview = entryValues.some(
    e => e.status === 'APPROVED' && e.requiresReview,
  );

  const approvedKeys = entryValues
    .filter(e => e.status === 'APPROVED' && e.finalKey)
    .map(e => e.finalKey!);

  return {
    entries,
    analyzeFile,
    removeEntry,
    allApproved,
    hasPending,
    hasRejected,
    hasUnsupported,
    hasMediaAwaitingReview,
    approvedKeys,
  };
}
